import { EmptyContractPartner, InvoiceAddressForm } from '../03_current_contract_partner/model/empty_contract_partner.model';
import {
  ContactDataIndividual,
  ContactDataOrganization,
  OccupiedContractPartner,
} from '../03_current_contract_partner/model/occupied_contract_partner.model';
import {
  ContractPartnerCompanyModel,
  ContractPartnerEmpty,
  ContractPartnerPrivateModel,
  VertragspartnerSelectionType,
} from '../04_new_contract_partner/new-contract-partner.utils';
import { NewContractPartner } from '../meterpoint-change-request.types';
import { EMPTY_VALUE, formatAddressForm } from '../meter-point-change-request.utils';
import { getAddressPostCodeAndStreetFormatted, getDayJsFromNgbDateStruct } from '@mwe/utils';
import { DATE_FORMAT_GERMAN } from '@shared-ikp/app.constants';
import { MeterPoint } from '../../../model/meter-point.model';

export function currentEmptyContractPartnerToFields(partner: EmptyContractPartner): InfoCardField[] {
  const emptyContractPartnerFields = [
    {
      label: 'meterPoint.meterPointChangeRequest.currentContractPartnerStep.empty.company',
      value: partner?.company || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-currentpartner-company',
    },
    {
      label: 'meterPoint.meterPointChangeRequest.currentContractPartnerStep.empty.customerNumber',
      value: partner?.customerNumber || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-currentpartner-customerNumber',
    },
    {
      label: 'meterPoint.meterPointChangeRequest.currentContractPartnerStep.empty.address',
      value: formatAddressForm(partner?.address),
      contentDivId: 'meterpoint-change-summary-currentpartner-address',
    },
  ];
  if (partner?.invoiceAddress) {
    emptyContractPartnerFields.push({
      label: 'meterPoint.meterPointChangeRequest.currentContractPartnerStep.empty.differentInvoiceAddress',
      value: formatAddressForm(partner.invoiceAddress.address),
      contentDivId: 'meterpoint-change-summary-currentpartner-invoiceAddress',
    });
  }
  return emptyContractPartnerFields;
}

export function newEmptyContractPartnerToFields(partner: ContractPartnerEmpty): InfoCardField[] {
  return [
    {
      label: 'meterPoint.meterPointChangeRequest.currentContractPartnerStep.empty.company',
      value: partner?.name || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-newpartner-company',
    },
    {
      label: 'meterPoint.meterPointChangeRequest.currentContractPartnerStep.empty.customerNumber',
      value: partner?.gpNr || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-newpartner-customerNumber',
    },
    {
      label: 'meterPoint.meterPointChangeRequest.currentContractPartnerStep.empty.address',
      value: formatAddressForm(partner?.address),
      contentDivId: 'meterpoint-change-summary-newpartner-address',
    },
    {
      label: 'meterPoint.meterPointChangeRequest.summary.newPartner.uid',
      value: partner?.uid || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-newpartner-uid',
    },
  ];
}

export function alternativeInvoicingAddressToFields(
  invoiceAddress: InvoiceAddressForm,
  selectedVertragspartner: VertragspartnerSelectionType,
): InfoCardField[] {
  if (!invoiceAddress) {
    return [];
  }

  const isEigenesUnternehmen = selectedVertragspartner === VertragspartnerSelectionType.EIGENES_UNTERNEHMEN;

  return isEigenesUnternehmen
    ? [
        {
          label: 'meterPoint.meterPointChangeRequest.currentContractPartnerStep.empty.company',
          value: invoiceAddress.company || EMPTY_VALUE,
          contentDivId: 'meterpoint-change-summary-altinvoice-company',
        },
        {
          label: 'meterPoint.meterPointChangeRequest.currentContractPartnerStep.empty.address',
          value: formatAddressForm(invoiceAddress.address),
          contentDivId: 'meterpoint-change-summary-altinvoice-address',
        },
      ]
    : [
        {
          label: 'meterPoint.meterPointChangeRequest.currentContractPartnerStep.empty.company',
          value: invoiceAddress.company || EMPTY_VALUE,
          contentDivId: 'meterpoint-change-summary-altinvoice-company',
        },
        {
          label: 'meterPoint.meterPointChangeRequest.currentContractPartnerStep.empty.customerNumber',
          value: invoiceAddress.gpNr || EMPTY_VALUE,
          contentDivId: 'meterpoint-change-summary-altinvoice-customerNumber',
        },
        {
          label: 'meterPoint.meterPointChangeRequest.currentContractPartnerStep.empty.address',
          value: formatAddressForm(invoiceAddress.address),
          contentDivId: 'meterpoint-change-summary-altinvoice-address',
        },
        {
          label: 'meterPoint.meterPointChangeRequest.summary.newPartner.uid',
          value: invoiceAddress.uid || EMPTY_VALUE,
          contentDivId: 'meterpoint-change-summary-altinvoice-uid',
        },
      ];
}

export function currentOccupiedContractPartnerToFields(partner: OccupiedContractPartner): InfoCardField[] {
  const currentOccupiedContractPartnerFields: InfoCardField[] = [];
  if (partner?.contactDataIndividual) {
    currentOccupiedContractPartnerFields.push(...contactDataIndividualToFields(partner.contactDataIndividual));
  } else if (partner?.contactDataOrganization) {
    currentOccupiedContractPartnerFields.push(...contactDataOrganizationToFields(partner.contactDataOrganization));
  }

  currentOccupiedContractPartnerFields.push({
    label: 'meterPoint.meterPointChangeRequest.summary.currentPartner.finalInvoice',
    value: formatAddressForm(partner?.invoiceAddress),
    contentDivId: 'meterpoint-change-summary-currentpartner-finalinvoice',
  });

  return currentOccupiedContractPartnerFields;
}

export function newContractPartnerToFields(meterpoint: MeterPoint, partner: NewContractPartner) {
  const partnerFields: InfoCardField[] = [];
  if (partner?.formData?.organizationPartner) {
    partnerFields.push(...contractPartnerOrganizationToFields(partner.formData.organizationPartner));
  } else if (partner?.formData?.privatePartner) {
    partnerFields.push(...contractPartnerPrivateToFields(partner.formData.privatePartner));
  }

  if (partner?.alternativeInvoicingAddress) {
    partnerFields.push({
      label: 'meterPoint.meterPointChangeRequest.summary.newPartner.differentInvoiceAddress',
      value: formatAddressForm(partner.alternativeInvoicingAddress.address),
      contentDivId: 'meterpoint-change-summary-newPartner-differentInvoiceAddress',
    });
  } else {
    partnerFields.push({
      label: 'meterPoint.meterPointChangeRequest.summary.newPartner.invoiceAddress',
      value: getAddressPostCodeAndStreetFormatted(meterpoint.adresse),
      contentDivId: 'meterpoint-change-summary-newPartner-invoiceAddress',
    });
  }

  return partnerFields;
}

function contactDataIndividualToFields(contact: ContactDataIndividual): InfoCardField[] {
  return [
    {
      label: 'meterPoint.meterPointChangeRequest.currentContractPartnerStep.contactData.firstName',
      value: contact?.firstName || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-currentpartner-firstname',
    },
    {
      label: 'meterPoint.meterPointChangeRequest.currentContractPartnerStep.contactData.lastName',
      value: contact?.lastName || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-currentpartner-lastname',
    },
    {
      label: 'meterPoint.meterPointChangeRequest.currentContractPartnerStep.contactData.email',
      value: contact?.email || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-currentpartner-email',
    },
    {
      label: 'meterPoint.meterPointChangeRequest.currentContractPartnerStep.contactData.phoneNumber',
      value: contact?.phoneNumber || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-currentpartner-phone',
    },
  ];
}

function contactDataOrganizationToFields(contact: ContactDataOrganization): InfoCardField[] {
  return [
    {
      label: 'meterPoint.meterPointChangeRequest.currentContractPartnerStep.contactData.companyName',
      value: contact?.name || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-currentpartner-companyname',
    },
    {
      label: 'meterPoint.meterPointChangeRequest.currentContractPartnerStep.contactData.firmenbuchNr',
      value: contact?.firmenbuchNr || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-currentpartner-companyregisternumber',
    },
    {
      label: 'meterPoint.meterPointChangeRequest.currentContractPartnerStep.contactData.uidNr',
      value: contact?.uid || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-currentpartner-uidnumber',
    },
    {
      label: 'meterPoint.meterPointChangeRequest.currentContractPartnerStep.contactData.email',
      value: contact?.email || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-currentpartner-email',
    },
    {
      label: 'meterPoint.meterPointChangeRequest.currentContractPartnerStep.contactData.phoneNumber',
      value: contact?.phoneNumber || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-currentpartner-phone',
    },
  ];
}

function contractPartnerOrganizationToFields(organizationPartner: ContractPartnerCompanyModel) {
  return [
    {
      label: 'meterPoint.meterPointChangeRequest.contractPartnerType.forms.company.name',
      value: organizationPartner?.name || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-newpartner-name',
    },
    {
      label: 'meterPoint.meterPointChangeRequest.contractPartnerType.forms.company.birthDate',
      value: getDayJsFromNgbDateStruct(organizationPartner?.birthDate)?.format(DATE_FORMAT_GERMAN) || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-newpartner-birthdate',
    },
    {
      label: 'meterPoint.meterPointChangeRequest.contractPartnerType.forms.company.uidNumber',
      value: organizationPartner?.uidNumber || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-newpartner-uidnumber',
    },
    {
      label: 'meterPoint.meterPointChangeRequest.contractPartnerType.forms.company.firmenbuchNumber',
      value: organizationPartner?.firmenbuchNumber || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-newpartner-firmenbuchnumber',
    },
    {
      label: 'meterPoint.meterPointChangeRequest.contractPartnerType.forms.company.email',
      value: organizationPartner?.email || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-newpartner-email',
    },
    {
      label: 'meterPoint.meterPointChangeRequest.contractPartnerType.forms.company.phone',
      value: organizationPartner?.phone || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-newpartner-phone',
    },
  ];
}

function contractPartnerPrivateToFields(privatePartner: ContractPartnerPrivateModel) {
  return [
    {
      label: 'meterPoint.meterPointChangeRequest.contractPartnerType.forms.privatePerson.birthDate.label',
      value: getDayJsFromNgbDateStruct(privatePartner?.birthDate)?.format(DATE_FORMAT_GERMAN) || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-newpartner-birthdate',
    },
    {
      label: null,
      value: null,
      contentDivId: null,
    },
    {
      label: 'meterPoint.meterPointChangeRequest.contractPartnerType.forms.privatePerson.firstName',
      value: privatePartner?.firstName || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-newpartner-firstname',
    },
    {
      label: 'meterPoint.meterPointChangeRequest.contractPartnerType.forms.privatePerson.lastName',
      value: privatePartner?.lastName || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-newpartner-lastname',
    },
    {
      label: 'meterPoint.meterPointChangeRequest.contractPartnerType.forms.privatePerson.email',
      value: privatePartner?.email || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-newpartner-email',
    },
    {
      label: 'meterPoint.meterPointChangeRequest.contractPartnerType.forms.privatePerson.phone',
      value: privatePartner?.phone || EMPTY_VALUE,
      contentDivId: 'meterpoint-change-summary-newpartner-phone',
    },
  ];
}

export interface InfoCardField {
  label: string;
  value: string;
  contentDivId: string;
}
